import { Intent } from '@blueprintjs/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '../../lib/queryKeys';
import { Notifications } from '../../notifications/components/Notifications';
import { observableToPromise } from '../../shared/observableToPromise';
import { type AccountResponse, postAccountSsoDisable } from '../api';
import type { Account } from '../model';

/**
 * Disables SAML SSO for an account via react-query (no Redux).
 *
 * The disable endpoint returns the updated SAML config, so on success we write
 * that payload straight into the account's react-query cache with setQueryData
 * rather than invalidating and refetching. Beyond avoiding a redundant round
 * trip, this is what makes the UI update reliably once deployed: the previous
 * invalidate-and-refetch approach could refetch from a read replica / cache
 * that hadn't yet caught up with the write, so the panel kept rendering the
 * stale (still-enabled) config. Writing the authoritative response is
 * deterministic and immune to that read-after-write window.
 */
export function useDisableSso(account: Account | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!account) {
        throw new Error('Account not found');
      }
      return observableToPromise(postAccountSsoDisable(account));
    },
    onSuccess: (samlConfig) => {
      if (!account) {
        return;
      }

      queryClient.setQueryData<AccountResponse | null>(queryKeys.accounts.detail(account._id), (previous) => {
        const cachedAccount = previous?.entities?.accounts?.[account._id];
        if (!previous || !cachedAccount) {
          return previous;
        }
        return {
          ...previous,
          entities: {
            ...previous.entities,
            accounts: {
              ...previous.entities.accounts,
              [account._id]: {
                ...cachedAccount,
                samlConfig,
              },
            },
          },
        };
      });

      Notifications.show({
        intent: Intent.SUCCESS,
        message: 'Disabled SSO successfully',
      });
    },
    onError: (error: unknown) => {
      const err = error as { message?: string | { [key: string]: string } };
      const message =
        (err.message && typeof err.message === 'object'
          ? (err.message as { [key: string]: string }).message
          : err.message) || 'Unknown error';
      Notifications.show({
        intent: Intent.DANGER,
        message: `Failed to disable SSO for account: ${message}`,
      });
    },
  });
}
